import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  TablePagination,
  InputAdornment,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api, { handleApiError } from '../utils/api';

const AdminUserPlanEditorPage = () => {
  const { t } = useTranslation();

  // Users list state with pagination
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Filter state
  const [selectedPlans, setSelectedPlans] = useState(['all']);
  const [availablePlans, setAvailablePlans] = useState([]);

  // Selected user and plan state
  const [selectedUser, setSelectedUser] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [newEndDate, setNewEndDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Success state
  const [successMessage, setSuccessMessage] = useState('');

  // Load available plans
  useEffect(() => {
    loadPlans();
  }, []);

  // Load users with pagination
  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, searchTerm, selectedPlans]);

  const loadPlans = async () => {
    try {
      const response = await api.get('/plans');
      if (response.data.success) {
        setAvailablePlans(response.data.data);
      }
    } catch (error) {
      console.error('Load plans error:', error);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    setUsersError('');

    try {
      const response = await api.get('/users', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm || undefined,
          status: 'active'
        }
      });

      if (response.data.success) {
        // Filter by selected plans if any (excluding 'all')
        let filteredUsers = response.data.data;
        if (selectedPlans.length > 0 && !selectedPlans.includes('all')) {
          filteredUsers = filteredUsers.filter(user =>
            user.membershipPlan && selectedPlans.includes(user.membershipPlan._id)
          );
        }
        setUsers(filteredUsers);
        setTotalUsers(response.data.pagination.total);
      }
    } catch (error) {
      console.error('Load users error:', error);
      setUsersError(handleApiError(error));
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = () => {
    setPage(0);
    loadUsers();
  };

  // Load user plan data
  const loadUserPlan = async (userId) => {
    setLoading(true);
    setError('');
    setPlanData(null);

    try {
      const response = await api.get(`/admin/users/${userId}/plan-edit`);

      if (response.data.success) {
        setPlanData(response.data.data);
      }
    } catch (error) {
      console.error('Load plan error:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  // Handle user selection
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSuccessMessage('');
    loadUserPlan(user._id);
  };

  // Open edit dialog
  const handleOpenEdit = () => {
    if (!planData) return;

    // Initialize selected facilities with all current facilities
    const currentFacilityIds = planData.currentPlan.facilities.map(f => f._id);
    setSelectedFacilities(currentFacilityIds);

    // Initialize end date
    setNewEndDate(new Date(planData.currentPlan.endDate));

    setEditDialogOpen(true);
    setSaveError('');
  };

  // Handle facility checkbox change
  const handleFacilityChange = (facilityId) => {
    setSelectedFacilities(prev => {
      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      } else {
        return [...prev, facilityId];
      }
    });
  };

  // Handle edit submit (open confirmation)
  const handleSubmitEdit = () => {
    // Only validate facilities if there are facilities available in the plan
    if (planData?.currentPlan.facilities && planData.currentPlan.facilities.length > 0) {
      if (selectedFacilities.length === 0) {
        setSaveError(t('admin:userPlanEditor.errors.noFacilitiesSelected'));
        return;
      }
    }

    if (!newEndDate) {
      setSaveError(t('admin:userPlanEditor.errors.noEndDate'));
      return;
    }

    // Check if end date is valid
    const startDate = new Date(planData.currentPlan.startDate);
    if (newEndDate <= startDate) {
      setSaveError(t('admin:userPlanEditor.errors.endDateBeforeStart'));
      return;
    }

    setEditDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  // Handle confirmed save
  const handleConfirmSave = async () => {
    setSaving(true);
    setSaveError('');

    try {
      // Convert end date to end of day in local timezone, then to ISO
      const endOfDay = new Date(newEndDate);
      endOfDay.setHours(23, 59, 59, 999);

      const response = await api.put(`/admin/users/${selectedUser._id}/plan-edit`, {
        facilityIds: selectedFacilities,
        membershipEndDate: endOfDay.toISOString()
      });

      if (response.data.success) {
        setConfirmDialogOpen(false);
        setSuccessMessage(t('admin:userPlanEditor.success.planUpdated'));

        // Reload plan data
        await loadUserPlan(selectedUser._id);

        // Reload users list to reflect changes
        await loadUsers();
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveError(handleApiError(error));
      setConfirmDialogOpen(false);
      setEditDialogOpen(true);
    } finally {
      setSaving(false);
    }
  };

  // Calculate date difference
  const calculateDateDifference = () => {
    if (!newEndDate || !planData) return 0;
    const oldEnd = new Date(planData.currentPlan.endDate);
    const diffTime = newEndDate - oldEnd;
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('admin:userPlanEditor.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('admin:userPlanEditor.subtitle')}
      </Typography>

      {/* Users List Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {t('admin:userPlanEditor.usersList.title')}
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loadingUsers}
          >
            {t('admin:common.refresh')}
          </Button>
        </Box>

        {/* Search and Filter Bar */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexDirection: { xs: 'column', md: 'row' }
        }}>
          <TextField
            fullWidth
            placeholder={t('admin:userPlanEditor.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
              <InputLabel id="plan-filter-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterIcon fontSize="small" />
                  {t('admin:userPlanEditor.filters.planFilter')}
                </Box>
              </InputLabel>
              <Select
                labelId="plan-filter-label"
                multiple
                value={selectedPlans}
                onChange={(e) => {
                  const value = e.target.value;
                  // If 'all' is selected, only keep 'all'
                  if (value.includes('all') && !selectedPlans.includes('all')) {
                    setSelectedPlans(['all']);
                  }
                  // If other plans are selected when 'all' is already selected, remove 'all'
                  else if (selectedPlans.includes('all') && value.length > 1) {
                    setSelectedPlans(value.filter(v => v !== 'all'));
                  }
                  // Normal case
                  else if (value.length === 0) {
                    setSelectedPlans(['all']);
                  } else {
                    setSelectedPlans(value);
                  }
                  setPage(0);
                }}
                input={<OutlinedInput label={t('admin:userPlanEditor.filters.planFilter')} />}
                renderValue={(selected) => {
                  if (selected.includes('all')) {
                    return <Chip label={t('admin:userPlanEditor.filters.allPlans')} size="small" />;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((planId) => {
                        const plan = availablePlans.find(p => p._id === planId);
                        return (
                          <Chip
                            key={planId}
                            label={plan?.name || planId}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  );
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={selectedPlans.includes('all')} />
                  {t('admin:userPlanEditor.filters.allPlans')}
                </MenuItem>
                {availablePlans.map((plan) => (
                  <MenuItem key={plan._id} value={plan._id}>
                    <Checkbox checked={selectedPlans.includes(plan._id)} />
                    {plan.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        {usersError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {usersError}
          </Alert>
        )}

        {loadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin:common.name')}</TableCell>
                    <TableCell>{t('admin:common.email')}</TableCell>
                    <TableCell>{t('admin:users.table.headers.membershipPlan')}</TableCell>
                    <TableCell>{t('admin:userPlanEditor.usersList.planEndDate')}</TableCell>
                    <TableCell align="center">{t('admin:common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {t('admin:userPlanEditor.usersList.noUsers')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow
                        key={user._id}
                        selected={selectedUser?._id === user._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => user.membershipPlan && handleSelectUser(user)}
                      >
                        <TableCell>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.membershipPlan ? (
                            <Chip
                              label={user.membershipPlan.name}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label={t('admin:users.table.noPlan')}
                              size="small"
                              color="default"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {user.membershipPlan && user.membershipEndDate ? (
                            new Date(user.membershipEndDate).toLocaleDateString()
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectUser(user);
                            }}
                            disabled={!user.membershipPlan}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalUsers}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={t('admin:common.rowsPerPage')}
            />
          </>
        )}
      </Paper>

      {/* Plan Details Section */}
      {selectedUser && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {t('admin:userPlanEditor.planDetails.title')} - {selectedUser.firstName} {selectedUser.lastName}
            </Typography>
            {planData && !loading && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleOpenEdit}
              >
                {t('admin:common.edit')}
              </Button>
            )}
          </Box>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {planData && !loading && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('admin:userPlanEditor.planDetails.planName')}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {planData.currentPlan.snapshot.name}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="text.secondary">
                      {t('admin:userPlanEditor.planDetails.duration')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {planData.currentPlan.snapshot.duration} {t('admin:userPlanEditor.planDetails.days')}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                      {t('admin:userPlanEditor.planDetails.startDate')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {new Date(planData.currentPlan.startDate).toLocaleDateString()}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                      {t('admin:userPlanEditor.planDetails.endDate')}
                    </Typography>
                    <Typography variant="body1">
                      {new Date(planData.currentPlan.endDate).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('admin:userPlanEditor.planDetails.facilities')}
                    </Typography>
                    {planData.currentPlan.facilities.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {planData.currentPlan.facilities.map((facility) => (
                          <Chip
                            key={facility._id}
                            label={facility.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('admin:userPlanEditor.planDetails.noFacilities')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {planData.nextPlan && (
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <InfoIcon color="info" sx={{ mr: 1, fontSize: 20 }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('admin:userPlanEditor.planDetails.nextPlan')}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>{planData.nextPlan.plan.name}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin:userPlanEditor.planDetails.startsOn')}: {new Date(planData.nextPlan.startDate).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('admin:userPlanEditor.editDialog.title')}</DialogTitle>
        <DialogContent dividers>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          {/* Facilities Selection */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
            {t('admin:userPlanEditor.editDialog.selectFacilities')}
          </Typography>
          {planData?.currentPlan.facilities && planData.currentPlan.facilities.length > 0 ? (
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormGroup>
                {planData.currentPlan.facilities.map((facility) => (
                  <FormControlLabel
                    key={facility._id}
                    control={
                      <Checkbox
                        checked={selectedFacilities.includes(facility._id)}
                        onChange={() => handleFacilityChange(facility._id)}
                      />
                    }
                    label={`${facility.name} (${facility.type})`}
                  />
                ))}
              </FormGroup>
            </FormControl>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {t('admin:userPlanEditor.editDialog.noFacilitiesAvailable')}
            </Alert>
          )}

          {/* End Date Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('admin:userPlanEditor.editDialog.selectEndDate')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('admin:userPlanEditor.editDialog.endDate')}
                value={newEndDate}
                onChange={(date) => {
                  console.log('DatePicker onChange:', date);
                  setNewEndDate(date);
                }}
                minDate={planData ? new Date(planData.currentPlan.startDate) : new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true
                  },
                  popper: {
                    placement: 'bottom-start'
                  }
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* Preview Changes */}
          {newEndDate && planData && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('admin:userPlanEditor.editDialog.preview')}
              </Typography>
              <Typography variant="body2">
                {t('admin:userPlanEditor.editDialog.oldEndDate')}: {new Date(planData.currentPlan.endDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                {t('admin:userPlanEditor.editDialog.newEndDate')}: {newEndDate.toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="primary">
                {t('admin:userPlanEditor.editDialog.difference')}: {calculateDateDifference()} {t('admin:userPlanEditor.planDetails.days')}
              </Typography>
              <Typography variant="body2">
                {t('admin:userPlanEditor.editDialog.facilitiesSelected')}: {selectedFacilities.length}
              </Typography>
              {planData.nextPlan && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {t('admin:userPlanEditor.editDialog.nextPlanWarning')}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitEdit}
            disabled={
              !newEndDate ||
              (planData?.currentPlan.facilities?.length > 0 && selectedFacilities.length === 0)
            }
          >
            {t('admin:userPlanEditor.editDialog.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !saving && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            {t('admin:userPlanEditor.confirmDialog.title')}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            {t('admin:userPlanEditor.confirmDialog.message')}
          </Typography>
          {planData?.nextPlan && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('admin:userPlanEditor.confirmDialog.nextPlanImpact')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={saving}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {saving ? t('admin:common.saving') : t('admin:common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUserPlanEditorPage;
